import {
  AutocompleteInput,
  BooleanInput,
  email,
  FormDataConsumer,
  maxLength,
  minLength,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
} from "react-admin";
import { Grid, useMediaQuery } from "@mui/material";
import { SectionTitle } from "features/SectionTitle";
import { administrationRequests } from "api/administrationApi";
import { GenderOptionsId } from "utils/choices";
import { erasmusRequests } from "api/erasmusApi";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { nameCyrillicRegexValidator, nameLatinRegexValidator, phoneValidator } from "utils/validations";
import DocumentFileInput from "../../Partials/DocumentFileInput";
import { MobilityVisitType } from "utils/constants";
import { settings } from "app";

const validateEndAfterStart = (value, allValues) => {
  if (value && allValues?.startDate && value < allValues.startDate) {
    return "Крайната дата не може да е преди началната";
  }
  return undefined;
};

const IncomingTechersSaveForm = ({ setCVId, setMobilityAgreementId, setAcceptanceLetterId }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  return (
    <SimpleForm>
      <Grid
        container
        spacing={4}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Данни за кандидата"
            paddingY={0}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <TextInput
            source="firstNameCyrillic"
            label="Име (кирилица)"
            validate={[minLength(2), maxLength(30), nameCyrillicRegexValidator]}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <TextInput
            source="lastNameCyrillic"
            label="Фамилия (кирилица)"
            validate={[minLength(2), maxLength(30), nameCyrillicRegexValidator]}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <TextInput
            source="firstNameLatin"
            label="Име (латиница)"
            validate={[minLength(2), maxLength(30), nameLatinRegexValidator]}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <TextInput
            source="lastNameLatin"
            label="Фамилия (латиница)"
            validate={[minLength(2), maxLength(30), nameLatinRegexValidator]}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            source="email"
            label="Имейл"
            validate={[required(), email()]}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            source="phoneNumber"
            validate={[phoneValidator, maxLength(50)]}
            label="Телефон"
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <SelectInput
            label="Пол"
            source="gender"
            choices={GenderOptionsId}
            validate={[required()]}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            source="position"
            label="Длъжност"
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            reference={administrationRequests.getCitizenshipSelect()}
            source="citizenship"
          >
            <AutocompleteInput
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
              optionText="name"
              optionValue="id"
              validate={required("Задължително")}
              label="Тип Гражданство ЕС / Извън ЕС"
              fullWidth
            />
          </ReferenceInput>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="mobilityVisitType"
            reference={erasmusRequests.getMobilityTypesSelect()}
          >
            <SelectInput
              label="Тип визита"
              optionText="nameBg"
              validate={required()}
            />
          </ReferenceInput>
        </Grid>
        <FormDataConsumer>
          {({ formData }) =>
            formData.mobilityVisitType === MobilityVisitType.BIP && (
              <>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <TextInput
                    source="bipName"
                    label="BIP име"
                    validate={[required("Задължително"), maxLength(30)]}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <TextInput
                    source="bipNumber"
                    label="BIP номер"
                    validate={[required("Задължително"), maxLength(20)]}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <CustomDateInput
                    source="startDate"
                    label="Начална дата"
                    validate={required("Задължително")}
                    required={true}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <CustomDateInput
                    source="endDate"
                    label="Крайна дата"
                    minDate={formData.startDate || undefined}
                    validate={[required("Задължително"), validateEndAfterStart]}
                    required={true}
                  />
                </Grid>
              </>
            )
          }
        </FormDataConsumer>

        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="universityId"
            reference={administrationRequests.getUniversitiesSelect()}
          >
            <AutocompleteInput
              label="Университет"
              optionText="name"
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
              validate={required()}
            />
          </ReferenceInput>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            source="erasmusCode"
            label="Еразъм код"
            validate={maxLength(50)}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="structuralUnitId"
            filter={{ filterByUser: true }}
            reference={administrationRequests.getStructuralUnitsSelect()}
          >
            <SelectInput
              label="Структурна единица"
              helperText={false}
              validate={required()}
            />
          </ReferenceInput>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="specialtyId"
            reference={administrationRequests.getSpecialitiesSelect()}
          >
            <SelectInput
              label="Факултет"
              style={{ minWidth: 280 }}
              validate={required()}
            />
          </ReferenceInput>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <BooleanInput
            source="wantsDormitory"
            label="Желае общежитие"
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={4}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Документи"
            paddingY={0}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Споразумение за мобилност"
            source="mobilityAgreementId"
            onUploadSuccess={setMobilityAgreementId}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="Писмо за приемане"
            source="acceptanceLetterId"
            onUploadSuccess={setAcceptanceLetterId}
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <DocumentFileInput
            label="CV"
            source="cvId"
            onUploadSuccess={setCVId}
          />
        </Grid>
      </Grid>
    </SimpleForm>
  );
};
export default IncomingTechersSaveForm;
